import { useCallback, useEffect, useRef, useState } from "react";

const TAP_THRESHOLD = 5;
const TAP_WINDOW_MS = 3_000;
const CORNER_SIZE_PX = 80;

export const useDiagnosticsTrigger = (): {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
} => {
  const [isOpen, setIsOpen] = useState(false);
  const tapTimestampsRef = useRef<number[]>([]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.ctrlKey || !e.shiftKey) return;
      if (e.key !== "D" && e.key !== "d") return;
      e.preventDefault();
      setIsOpen((v) => !v);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  useEffect(() => {
    const handler = (e: PointerEvent) => {
      const inCorner =
        e.clientX >= window.innerWidth - CORNER_SIZE_PX &&
        e.clientY >= window.innerHeight - CORNER_SIZE_PX;
      const now = Date.now();
      if (!inCorner) {
        tapTimestampsRef.current = [];
        return;
      }
      tapTimestampsRef.current = tapTimestampsRef.current.filter((t) => now - t <= TAP_WINDOW_MS);
      tapTimestampsRef.current.push(now);
      if (tapTimestampsRef.current.length >= TAP_THRESHOLD) {
        tapTimestampsRef.current = [];
        setIsOpen(true);
      }
    };
    window.addEventListener("pointerdown", handler, { capture: true });
    return () => window.removeEventListener("pointerdown", handler, { capture: true });
  }, []);

  return { isOpen, open, close, toggle };
};
